import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { ArrowLeft, FileText, Shield, Users, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const TermosDeUso = () => {
  const { siteSettings } = useContext(AuthContext);
  const siteName = siteSettings?.site_name || 'Nossa Feira';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Termos de Uso</h1>
              <p className="text-gray-600">Última atualização: Outubro de 2024</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          
          {/* Introduction */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                1. Introdução
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <p>
                Bem-vindo ao {siteName}! Estes Termos de Uso regem o uso da nossa plataforma digital que conecta produtores rurais locais diretamente aos consumidores. Ao acessar ou utilizar nossos serviços, você concorda em cumprir e ficar vinculado a estes termos.
              </p>
              <p>
                O {siteName} é uma plataforma que facilita a venda direta de produtos agrícolas frescos, promovendo o desenvolvimento sustentável e fortalecendo a economia local.
              </p>
            </CardContent>
          </Card>

          {/* User Types */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                2. Tipos de Usuários
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>2.1 Consumidores</h4>
              <ul>
                <li>Podem se cadastrar diretamente na plataforma</li>
                <li>Podem navegar e visualizar produtos disponíveis</li>
                <li>Podem entrar em contato com produtores via WhatsApp</li>
                <li>Podem avaliar produtos e produtores</li>
              </ul>
              
              <h4>2.2 Produtores</h4>
              <ul>
                <li>Cadastro realizado exclusivamente por administradores</li>
                <li>Podem listar seus produtos para venda</li>
                <li>Devem fornecer informações precisas sobre seus produtos</li>
                <li>São responsáveis pelo atendimento direto aos consumidores</li>
              </ul>

              <h4>2.3 Administradores</h4>
              <ul>
                <li>Gerenciam a plataforma e seus usuários</li>
                <li>Aprovam ou rejeitam produtos listados</li>
                <li>Podem criar contas para produtores</li>
                <li>Monitoram o cumprimento destes termos</li>
              </ul>
            </CardContent>
          </Card>

          {/* Responsibilities */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-600" />
                3. Responsabilidades dos Usuários
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>3.1 Responsabilidades Gerais</h4>
              <ul>
                <li>Fornecer informações verdadeiras e atualizadas</li>
                <li>Manter a confidencialidade de suas credenciais de acesso</li>
                <li>Usar a plataforma apenas para fins legítimos</li>
                <li>Respeitar os direitos de outros usuários</li>
              </ul>

              <h4>3.2 Responsabilidades dos Produtores</h4>
              <ul>
                <li>Garantir a qualidade e procedência dos produtos listados</li>
                <li>Manter informações de contato atualizadas</li>
                <li>Cumprir com as regulamentações sanitárias aplicáveis</li>
                <li>Responder prontamente às solicitações dos consumidores</li>
              </ul>

              <h4>3.3 Responsabilidades dos Consumidores</h4>
              <ul>
                <li>Avaliar produtos de forma honesta e construtiva</li>
                <li>Tratar produtores com respeito e cortesia</li>
                <li>Reportar qualquer irregularidade encontrada</li>
              </ul>
            </CardContent>
          </Card>

          {/* Platform Rules */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>4. Uso da Plataforma</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>4.1 Listagem de Produtos</h4>
              <p>
                Todos os produtos devem passar por aprovação administrativa antes de serem exibidos na plataforma. Os produtos devem ser agrícolas, frescos e produzidos localmente.
              </p>

              <h4>4.2 Comunicação</h4>
              <p>
                A comunicação entre produtores e consumidores ocorre diretamente via WhatsApp. A plataforma não se responsabiliza pelo conteúdo dessas conversas.
              </p>

              <h4>4.3 Avaliações</h4>
              <p>
                As avaliações devem ser baseadas na experiência real com o produto e/ou produtor. Avaliações falsas ou maliciosas podem resultar na suspensão da conta.
              </p>
            </CardContent>
          </Card>

          {/* Limitations */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>5. Limitações e Isenções</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>5.1 Papel da Plataforma</h4>
              <p>
                O {siteName} atua apenas como intermediário, conectando produtores e consumidores. Não somos responsáveis por:
              </p>
              <ul>
                <li>Qualidade, segurança ou legalidade dos produtos</li>
                <li>Transações comerciais entre usuários</li>
                <li>Disputas entre produtores e consumidores</li>
                <li>Cumprimento de regulamentações por parte dos produtores</li>
              </ul>

              <h4>5.2 Disponibilidade do Serviço</h4>
              <p>
                Nos esforçamos para manter a plataforma disponível, mas não garantimos disponibilidade ininterrupta. Podemos realizar manutenções e atualizações conforme necessário.
              </p>
            </CardContent>
          </Card>

          {/* Changes and Contact */}
          <Card className="card-feira">
            <CardHeader>
              <CardTitle>6. Modificações e Contato</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-gray max-w-none">
              <h4>6.1 Alterações nos Termos</h4>
              <p>
                Reservamos o direito de modificar estes termos a qualquer momento. Usuários serão notificados sobre mudanças significativas através da plataforma.
              </p>

              <h4>6.2 Contato</h4>
              <p>
                Para dúvidas sobre estes termos ou sobre o uso da plataforma, entre em contato conosco através do email {siteSettings?.contact_email || 'contato@nossafeira.com'} ou telefone {siteSettings?.contact_phone || '+55 (77) 9 9999-9999'}.
              </p>

              <h4>6.3 Lei Aplicável</h4>
              <p>
                Estes termos são regidos pelas leis brasileiras. Disputas serão resolvidas nos tribunais competentes de {siteSettings?.contact_address || 'Bom Jesus da Lapa, BA'}.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 p-6 bg-green-50 rounded-lg">
          <p className="text-center text-gray-600 text-sm">
            Ao continuar usando o {siteName}, você concorda com estes Termos de Uso e nossa{' '}
            <Link to="/politica-de-privacidade" className="text-green-600 hover:underline">
              Política de Privacidade
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermosDeUso;